import type { IndustrySetupContext, IndustrySetupInput, IndustrySetupSelections } from "./types";
import { chooseExistingSetupOption } from "./personalTrainerSelections";
import { courierPlatforms } from "@/app/modules/courier/platforms";

export type CourierSetup = NonNullable<IndustrySetupInput["courierSetup"]>;
export type CourierSetupMember = CourierSetup["members"][number];
export type CourierSetupVehicle = CourierSetup["vehicles"][number];

export function createCourierInput(displayName: string, _translate?: (key: string) => string, person?: { name: string; email: string }): IndustrySetupInput {
  return {
    displayName,
    selectedRecommendedAppCodes: [],
    courierSetup: {
      mode: "solo",
      color: "#6366F1",
      members: [{ key: "courier-1", name: person?.name ?? "", email: person?.email ?? null, platforms: ["own"], vehicleKey: null }],
      vehicles: [],
    },
  };
}

const validKey = (key: string) => /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(key);
const validName = (name: string, max: number) => name.trim().length > 0 && name.trim().length <= max;

export function validCourierMembers(setup: CourierSetup | null | undefined): boolean {
  if (!setup || setup.members.length < 1 || setup.members.length > 30) return false;
  const employees = setup.members.flatMap((member) => member.employeeUuid ? [member.employeeUuid] : []);
  return new Set(setup.members.map((member) => member.key)).size === setup.members.length
    && new Set(employees).size === employees.length
    && setup.members.every((member) => validKey(member.key) && validName(member.name, 160)
      && (!member.phone || member.phone.trim().length <= 40)
      && (!member.email || (member.email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email))));
}

export function validCourierPlatforms(setup: CourierSetup | null | undefined): boolean {
  return validCourierMembers(setup) && Boolean(setup?.members.every((member) => member.platforms.length > 0
    && new Set(member.platforms).size === member.platforms.length
    && member.platforms.every((platform) => courierPlatforms.includes(platform))));
}

export function validCourierSetup(input: IndustrySetupInput): boolean {
  if (input.installOnly) return true;
  const setup = input.courierSetup;
  if (!setup || !validName(input.displayName, 160) || !validCourierPlatforms(setup) || setup.vehicles.length > 30) return false;
  if (setup.color && !/^#[0-9a-fA-F]{6}$/.test(setup.color)) return false;
  const keys = new Set(setup.vehicles.map((vehicle) => vehicle.key));
  return keys.size === setup.vehicles.length
    && setup.members.every((member) => !member.vehicleKey || keys.has(member.vehicleKey))
    && setup.vehicles.every((vehicle) => validKey(vehicle.key) && validName(vehicle.name, 190)
      && setup.members.some((member) => member.vehicleKey === vehicle.key));
}

export function removeUnusedCourierVehicles(setup: CourierSetup): CourierSetup {
  return { ...setup, vehicles: setup.vehicles.filter((vehicle) => setup.members.some((member) => member.vehicleKey === vehicle.key)) };
}

export function resetCourierWorkspaceSelections(setup: CourierSetup | null | undefined): CourierSetup | undefined {
  if (!setup) return undefined;
  const vehicles = setup.vehicles.filter((vehicle) => !vehicle.existingUuid);
  return {
    ...setup,
    vehicles,
    members: setup.members.map((member) => ({
      ...member,
      employeeUuid: null,
      vehicleKey: vehicles.some((vehicle) => vehicle.key === member.vehicleKey) ? member.vehicleKey : null,
    })),
  };
}

export function resolveCourierSelections(context: IndustrySetupContext, input: IndustrySetupInput) {
  const companyUuid = chooseExistingSetupOption(context.companies, input.companyUuid ?? context.companyUuid);
  const selections: IndustrySetupSelections = {
    companyUuid, branchUuid: null, coachProfileUuid: null, bookingPortalUuid: null,
    bookingResourceUuid: null, bookingServiceUuid: null, clientPortalUuid: null,
  };
  return { selections, needsSelection: context.companies.length > 1 && companyUuid === null };
}

export function parseCourierSetupDraft(value: unknown): CourierSetup | undefined {
  const object = (item: unknown): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && !Array.isArray(item);
  const optionalString = (item: unknown) => item == null || typeof item === "string";
  if (!object(value) || !Array.isArray(value.members) || !Array.isArray(value.vehicles)
    || value.members.length < 1 || value.members.length > 30 || value.vehicles.length > 30
    || (value.mode !== "solo" && value.mode !== "team") || !optionalString(value.color)) return undefined;
  if (!value.members.every((member) => object(member) && typeof member.key === "string" && typeof member.name === "string"
    && Array.isArray(member.platforms) && member.platforms.every((platform: unknown) => typeof platform === "string" && courierPlatforms.some((known) => known === platform))
    && [member.phone, member.email, member.employeeUuid, member.vehicleKey].every(optionalString))) return undefined;
  if (!value.vehicles.every((vehicle) => object(vehicle) && typeof vehicle.key === "string" && typeof vehicle.name === "string"
    && optionalString(vehicle.existingUuid))) return undefined;
  return {
    mode: value.mode,
    members: value.members as CourierSetupMember[],
    vehicles: value.vehicles as CourierSetupVehicle[],
    color: typeof value.color === "string" ? value.color : null,
  };
}
